import { PillarDefinition } from '../../types';
import { getFormattingRules, getGoldStandard } from './MasterSceneKnowledge';

// ============================================================
// FORMATTING STANDARDS — imported from MasterSceneKnowledge.ts
// (Backward-compatible reexports)
// ============================================================

export const FORMATTING_STANDARDS_KNOWLEDGE_PT = getFormattingRules('pt');
export const FORMATTING_STANDARDS_KNOWLEDGE_EN = getFormattingRules('en');
export const FORMATTING_STANDARDS_KNOWLEDGE_ES = getFormattingRules('es');
export const FORMATTING_STANDARDS_KNOWLEDGE_FR = getFormattingRules('fr');

export const FORMATTING_STANDARDS_KNOWLEDGE = FORMATTING_STANDARDS_KNOWLEDGE_EN; // Default export for legacy

export function getFormattingKnowledge(language: string): string {
   return getFormattingRules(language);
}

// ============================================================
// GOLD STANDARD — imported from MasterSceneKnowledge.ts
// (Backward-compatible reexports)
// ============================================================

export const GOLD_STANDARD_EXAMPLES_PT = getGoldStandard('pt');
export const GOLD_STANDARD_EXAMPLES_EN = getGoldStandard('en');
export const GOLD_STANDARD_EXAMPLES_ES = getGoldStandard('es');
export const GOLD_STANDARD_EXAMPLES_FR = getGoldStandard('fr');

export const SCRIPT_AUDITING_KNOWLEDGE = `
# Script Auditing Protocol

## Phase 1: Technical Compliance(The Compiler)
Objective: Ensure the script is technically filmable and correctly formatted.
   Reference: FORMATTING STANDARDS.

1. ** Syntax Scan:** Check for correct Slugline format(\`INT. LOC - TIME\`), Character Casing, and Paragraph lengths.
2. **The Camera/Mic Rule:** Scan Action lines. If a line describes a thought/emotion/abstraction that cannot be seen or heard, flag it.
3. **Formatting Hygiene (V.O. Check):** Detect "glue" errors or illegal blank lines between Character names and Dialogue. If a block of V.O. text is being treated as Action, mark it as a CRITICAL fix.

## Phase 2: Narrative Logic (The Story Editor)
Objective: Ensure the story makes sense internally. (Causality, not Taste).

1. **Plot Holes:** Events without cause or contradicting world rules.
2. **Character Consistency:** Actions contradicting motivation.
`;

export const METHOD_MCKEE_RULES = `
"STORY" PRINCIPLES (Robert McKee):
1. STRUCTURE AND CHANGE: Structure is a selection of events from the characters' lives composed in a strategic sequence to arouse specific emotions and express a view of life.
2. VALUE CHARGE (+/-): Every scene must turn a value in the character's life from positive to negative or vice versa. If there is no value change, it is not a scene.
3. THE GAP: Story is born in the space where the subjective and objective touch. The protagonist pursues an object of desire, acts, and the world reacts differently than expected (The Gap).
4. ARCHPLOT: Causality, Closed Ending, Linear Time, External Conflict, Single and Active Protagonist.
5. TEXT AND SUBTEXT: If the scene is about what the scene is about, you're on the wrong path. The real action occurs in the subtext.
6. INCITING INCIDENT: Radically unbalances the protagonist's life.
`;

export const METHOD_FIELD_RULES = `
THE PARADIGM (Syd Field):
1. THREE-ACT STRUCTURE:
   - Act I (Setup): Presentation of the character, premise, and situation. (pp. 1-30)
   - Act II (Confrontation): The character faces obstacles to achieve their dramatic need. (pp. 30-90)
   - Act III (Resolution): The conclusion of the story. (pp. 90-120)
2. PLOT POINTS:
   - Plot Point I (p. 25-27): The hook that turns the story into Act II.
   - Plot Point II (p. 85-90): The event that turns the story toward the Resolution.
3. CHARACTER IS ACTION: What a person does is what they are, not what they say.
4. DRAMATIC NEED: Define what the character wants to overcome, win, or achieve.
5. KNOW THE ENDING: You cannot start writing without knowing the ending. It is the context that holds the story together.
`;

export const METHOD_HYBRID_RULES = `
META-METHODOLOGY (SYNERGY MODE - AUTEUR.OS):

MASTER DIRECTIVE: Use the structural rigidity of Syd Field for the macro skeleton and the psychodynamic depth of Robert McKee for the micro tissue (scenes).

FUSION LOGIC:
1. ARCHITECTURE (Macro): Use Field's "Paradigm" to ensure pacing.
   - Verify if the Inciting Incident occurs early and if Plot Points I and II are anchoring the act transitions.
   - Use Field's page definitions as pacing goals, not absolute rules (adapted via McKee's "Form, not Formula" principle).

2. SCENE DYNAMICS (Micro): Within Field's structure, apply McKee's "Value Charge."
   - Ensure each story beat is not just informative (exposition) but changes the polarity (+/-) of the character's life.
   - If a scene follows Field's structure but feels flat, apply McKee's "The Gap" principle: Make the world react unexpectedly to the protagonist's action.

3. CHARACTER:
   - Define the "Dramatic Need" (Field) for the conscious goal.
   - Define the "Unconscious Desire" (McKee) to add contradiction and dimension.
   - The final conflict must force a choice between "irreconcilable goods" or the "lesser of two evils" (McKee), occurring at the climax of Act III (Field).

RESULT: A story with a commercially viable skeleton (Hollywood Classic) but with an artistically resonant soul (Archplot/Deep Structure).
`;

export const PLOT_TWIST_KNOWLEDGE = `
PLOT TWIST THEORY (STRUCTURAL TOOL):

A Plot Twist is only considered high-quality if it simultaneously meets the following criteria:

1. RECONTEXTUALIZATION OF THE PAST:
The twist forces the audience to reinterpret previous events in a new light.
Key Question: "What I thought was true is no longer true?"
If it only surprises without changing the meaning of past scenes, it's a failure.

2. HIDDEN TRUTH, NOT NEW INFORMATION:
It must reveal something that was already latent/planted, not something that appears out of nowhere (Deus Ex Machina).
Do not use new characters, new rules, or unprepared information to resolve the twist.
Key Question: "Could this have been discovered earlier if I paid attention?"

3. DIRECTED EMOTIONAL IMPACT:
The reversal must provoke a clear dominant emotion (shock, tragedy, irony, catharsis, or moral horror).
Surprise without emotion must be rejected.

4. COHERENCE WITH THE WORLD'S RULES:
The twist cannot break the rules of the universe, character logic, or the tone of the story. If it requires an "exception" to established logic, it is a narrative cheat.

5. NARRATIVE VECTOR CHANGE:
After the twist, the story must change direction. It must reverse goals, allies/enemies, victim/agent, or the truth about motivations.
If nothing changes structurally, the twist is weak.

6. CONCEPTUAL SIMPLICITY:
It must be summarizable in a single clear sentence. Complexity that requires long explanations is artificial.

7. REAL NARRATIVE RISK:
It must challenge genre expectations and avoid known clichés. Do not choose the "safest" solution.

PLOT TWIST ALIGNMENT BY GENRE:
Before generating, identify the dominant genre and obey its laws:

- THRILLER (Paranoia/Threat): Reverse pursuer/pursued or reveal the hero's unconscious collaboration with evil. Question: "Who was really in control?" | Avoid: Long explanations.
- DRAMA (Identity/Guilt): Reveal a devastating moral truth or reverse victim/responsible. Question: "What can the character no longer deny about themselves?" | Avoid: Saving coincidences.
- SCI-FI (Reality/World): Redefine reality or reveal a hidden layer of the system. Question: "What if the world always worked this way and we didn't know?" | Avoid: New rules at the end.
- MYSTERY (Truth/Evidence): Visible answer all along or biased investigator. Question: "What clue was ignored for appearing too obvious?" | Avoid: Random killer.
- HORROR (Fear/Control): Inevitable threat or escape as part of the trap. Question: "What if there was no way out from the beginning?" | Avoid: Neutralizing the horror.
- FANTASY (Destiny/Myth): Subvert prophecy or show a hidden cost of power. Question: "What if destiny didn't mean what everyone believed?" | Avoid: Obvious chosen one.

SELECTION RULE: Prioritize the dominant emotional genre. The twist must reinforce the genre's promise.
KEY PHRASE: "This Plot Twist must surprise without betraying the genre."

FINAL EVALUATION RULE (AI SELF-CRITICISM):
Before delivering, the AI must ask itself: "If I were the viewer, would I have predicted this?"
- If yes: Generate another twist.
- If maybe: Strengthen the reversal.
- If no, but it makes complete sense (Surprising, but Inevitable): Approved.

QUALITY DIRECTIVE:
Always prefer: Meaning > Surprise | Impact > Complexity | Consequence > Explanation.
`;

export const PLANT_AND_PAYOFF_KNOWLEDGE = `
PLANT AND PAYOFF THEORY (NARRATIVE COHESION ENGINEERING):

The Plant and Payoff operates under the "contract with the audience" rule: nothing enters the story for free.

1. THE PLANT PHASE (The Sowing)
The secret is Camouflage through Immediate Utility.
- The Object/Information: Insert a piece of data, object, skill, or personality trait.
- The Camouflage: The Plant must have a function in the current scene that justifies its presence, different from its final critical function.
- Example: The hero refuses peanuts at a dinner (social embarrassment = camouflage). The audience registers the allergy but focuses on the embarrassment.

2. THE GAP (The Spacing)
You need time and narrative noise between the Plant and the Payoff.
- The Function: To make the audience forget the importance of the data but retain the memory of the data.

3. THE PAYOFF PHASE (The Harvest)
The data returns to solve a critical problem, usually in the Climax.
- The Recontextualization: The data assumes its true function.
- The Satisfaction: It feels inevitable, not luck (Deus Ex Machina).

STRUCTURE TYPES:
A. The "Chekhov’s Gun" (Physical): If there's a gun on the wall in Act 1, it must fire in Act 3.
B. The "Skill Set" (Skill): Establish an apparently useless skill at the beginning that saves a life at the end (e.g., Karate Kid - painting the fence becomes blocking).
C. The Emotional/Thematic: A belief stated at the beginning gains new meaning at the end.

RISKS:
- Obvious Plant: Focusing too much on the object without camouflage.
- Weak Payoff: The resolution is too small for the preparation.
- Deus Ex Machina: Using a payoff without having planted it before (Cheating).
`;

export const AUTEUR_PILLARS_KNOWLEDGE_PT = `
[AUTEUR.OS MASTER CLASS - 27 PILARES DE CRIAÇÃO DE ROTEIRO]

1. Protagonista: Personagem central que conduz a narrativa. (Uso: Filme, Animação, Documentário, Publicitário)
2. Objetivo: Meta clara e mensurável do protagonista. (Uso: Filme, Animação, Publicitário)
3. Antagonista / Força Opositora: Força que impede o progresso do protagonista. (Uso: Filme, Animação, Publicitário)
4. Conflito: Tensão principal da história. (Uso: Todos)
5. Stakes: O que está em jogo se o protagonista falhar. (Uso: Filme, Animação, Publicitário)
6. Cenário / Era: Onde e quando a história acontece. (Uso: Todos)
7. Tema: Ideia central ou argumento da obra. (Uso: Filme, Animação, Documentário)
8. Desejo Interno: Necessidade emocional do protagonista. (Uso: Filme, Animação)
9. Falha Trágica: Defeito que gera conflito. (Uso: Filme, Animação)
10. Arco do Personagem: Transformação ao longo da história. (Uso: Filme, Animação)
11. Incidente Incitante: Evento que inicia a jornada. (Uso: Filme, Animação, Publicitário)
12. Plano do Protagonista: Estratégia para atingir o objetivo. (Uso: Filme, Animação)
13. Obstáculos Progressivos: Dificuldades crescentes. (Uso: Filme, Animação)
14. Ponto de Não Retorno: Decisão irreversível. (Uso: Filme, Animação)
15. Clímax: Confronto máximo. (Uso: Filme, Animação, Publicitário)
16. Resolução: Novo equilíbrio final. (Uso: Filme, Animação, Publicitário)
17. Motivo Recorrente: Elemento simbólico repetido. (Uso: Filme, Animação)
18. Ironia Dramática: Contraste entre intenção e resultado. (Uso: Filme, Animação)
19. Regra Moral / Custo Ético: Preço de vencer. (Uso: Filme, Animação)
20. Promessa de Gênero: Expectativa criada no público. (Uso: Todos)
21. Tom: Atitude emocional da narrativa. (Uso: Todos)
22. Ritmo / Escalada: Progressão da tensão. (Uso: Filme, Animação, Publicitário)
23. Gatilho Emocional do Público: Emoção principal provocada. (Uso: Todos)
24. Plant & Payoff: Preparação e recompensa narrativa. (Uso: Filme, Animação)
25. Ponto de Vista Narrativo: Quem conduz emocionalmente. (Uso: Filme, Animação, Documentário)
26. Mundo em Conflito: Como o mundo reage. (Uso: Filme, Animação)
27. Final Emocional: Sensação deixada no público. (Uso: Todos)
`;

export const AUTEUR_PILLARS_KNOWLEDGE_EN = `
[AUTEUR.OS MASTER CLASS - 27 PILLARS OF SCRIPT CREATION]

1. Protagonist: Central character who drives the narrative. (Use: Film, Animation, Documentary, Advertisement)
2. Goal: Clear and measurable goal of the protagonist. (Use: Film, Animation, Advertisement)
3. Antagonist / Opposing Force: Force that prevents the protagonist's progress. (Use: Film, Animation, Advertisement)
4. Conflict: Main tension of the story. (Use: All)
5. Stakes: What is at risk if the protagonist fails. (Use: Film, Animation, Advertisement)
6. Setting / Era: Where and when the story takes place. (Use: All)
7. Theme: Central idea or argument of the work. (Use: Film, Animation, Documentary)
8. Internal Desire: Emotional need of the protagonist. (Use: Film, Animation)
9. Tragic Flaw: Defect that generates conflict. (Use: Film, Animation)
10. Character Arc: Transformation throughout the story. (Use: Film, Animation)
11. Inciting Incident: Event that starts the journey. (Use: Film, Animation, Advertisement)
12. Protagonist's Plan: Strategy to achieve the goal. (Use: Film, Animation)
13. Progressive Obstacles: Increasing difficulties. (Use: Film, Animation)
14. Point of No Return: Irreversible decision. (Use: Film, Animation)
15. Climax: Maximum confrontation. (Use: Film, Animation, Advertisement)
16. Resolution: New final balance. (Use: Film, Animation, Advertisement)
17. Recurring Motif: Repeated symbolic element. (Use: Film, Animation)
18. Dramatic Irony: Contrast between intention and result. (Use: Film, Animation)
19. Moral Rule / Ethical Cost: Price of winning. (Use: Film, Animation)
20. Genre Promise: Expectation created in the audience. (Use: All)
21. Tone: Emotional attitude of the narrative. (Use: All)
22. Pacing / Escalation: Progression of tension. (Use: Film, Animation, Advertisement)
23. Emotional Trigger: Main emotion provoked in the audience. (Use: All)
24. Plant & Payoff: Narrative preparation and reward. (Use: Film, Animation)
25. Narrative Point of View: Who leads emotionally. (Use: Film, Animation, Documentary)
26. World in Conflict: How the world reacts. (Use: Film, Animation)
27. Emotional Ending: Feeling left in the audience. (Use: All)
`;

export const AUTEUR_PILLARS_KNOWLEDGE_ES = `
[AUTEUR.OS MASTER CLASS - 27 PILARES DE CREACIÓN DE GUION]

1. Protagonista: Personaje central que conduce la narrativa. (Uso: Cine, Animación, Documental, Publicidad)
2. Objetivo: Meta clara y mensurable del protagonista. (Uso: Cine, Animación, Publicidad)
3. Antagonista / Fuerza Opositora: Fuerza que impide el progreso del protagonista. (Uso: Cine, Animación, Publicidad)
4. Conflicto: Tensión principal de la historia. (Uso: Todos)
5. Stakes: Lo que está en juego si el personaje falla. (Uso: Cine, Animación, Publicidad)
6. Escenario / Era: Dónde y cuándo ocurre la historia. (Uso: Todos)
7. Tema: Idea central o argumento de la obra. (Uso: Cine, Animación, Documental)
8. Deseo Interno: Necesidad emocional del protagonista. (Uso: Cine, Animación)
9. Falla Trágica: Defecto que genera el conflicto. (Uso: Cine, Animación)
10. Arco del Personaje: Transformación a lo largo de la historia. (Uso: Cine, Animación)
11. Incidente Incitante: Evento que inicia el viaje. (Uso: Cine, Animación, Publicidad)
12. Plan del Protagonista: Estrategia para lograr el objetivo. (Uso: Cine, Animación)
13. Obstáculos Progresivos: Dificultades crecientes. (Uso: Cine, Animación)
14. Punto de No Retorno: Decisión irreversible. (Uso: Cine, Animación)
15. Clímax: Confrontación máxima. (Uso: Cine, Animación, Publicidad)
16. Resolución: Nuevo equilibrio final. (Uso: Cine, Animación, Publicidad)
17. Motivo Recurrente: Elemento simbólico repetido. (Uso: Cine, Animación)
18. Ironía Dramática: Contraste entre intención y resultado. (Uso: Cine, Animación)
19. Regla Moral / Costo Ético: Precio de ganar. (Uso: Cine, Animación)
20. Promesa de Género: Expectativa creada en el público. (Uso: Todos)
21. Tono: Actitud emocional de la narrativa. (Uso: Todos)
22. Ritmo / Escalada: Progresión de la tensión. (Uso: Cine, Animación, Publicidad)
23. Gatillo Emocional: Emoción principal provocada. (Uso: Todos)
24. Plant & Payoff: Preparación y recompensa narrativa. (Uso: Cine, Animación)
25. Punto de Vista Narrativo: Quién conduce emocionalmente. (Uso: Cine, Animación, Documental)
26. Mundo en Conflicto: Cómo el mundo reacciona. (Uso: Cine, Animación)
27. Final Emocional: Sensación dejada en el público. (Uso: Todos)
`;

export const AUTEUR_PILLARS_KNOWLEDGE_FR = `
[AUTEUR.OS MASTER CLASS - 27 PILIERS DE CRÉATION DE SCÉNARIO]

1. Protagoniste : Personnage central qui conduit le récit. (Usage : Film, Animation, Documentaire, Publicité)
2. Objectif : But clair et mesurable du protagoniste. (Usage : Film, Animation, Publicité)
3. Antagoniste / Force d'Opposition : Force qui empêche la progression du protagoniste. (Usage : Film, Animation, Publicité)
4. Conflit : Tension principale de l'histoire. (Usage : Tous)
5. Enjeux (Stakes) : Ce qui est en jeu si le protagoniste échoue. (Usage : Film, Animation, Publicité)
6. Décor / Époque : Où et quand l'histoire se déroule. (Usage : Tous)
7. Thème : Idée centrale ou argument de l'œuvre. (Usage : Film, Animation, Documentaire)
8. Désir Interne : Besoin émotionnel du protagoniste. (Usage : Film, Animation)
9. Faille Tragique : Défaut qui génère le conflit. (Usage : Film, Animation)
10. Arc de Personnage : Transformation tout au long de l'histoire. (Usage : Film, Animation)
11. Incident Déclencheur : Événement qui lance le voyage. (Usage : Film, Animation, Publicité)
12. Plan du Protagoniste : Stratégie pour atteindre l'objectif. (Usage : Film, Animation)
13. Obstacles Progressifs : Difficultés croissantes. (Usage : Film, Animation)
14. Point de Non-Retour : Decisión irréversível. (Usage : Film, Animation)
15. Climax : Confrontation maximale. (Usage : Film, Animation, Publicité)
16. Résolution : Nouvel équilibre final. (Usage : Film, Animation, Publicité)
17. Motif Récurrent : Élément symbolique répété. (Usage : Film, Animation)
18. Ironie Dramática : Contraste entre intention et résultat. (Usage : Film, Animation)
19. Règle Morale / Coût Éthique : Prix de la victoire. (Usage : Film, Animation)
20. Promessa de Genre : Attente créée chez le public. (Usage : Tous)
21. Ton : Attitude émotionnelle du récit. (Usage : Tous)
22. Rythme / Escalade : Progression de la tension. (Usage : Film, Animation, Publicité)
23. Déclencheur Émotionnel : Émotion principale provoquée chez le public. (Usage : Tous)
24. Plant & Payoff : Préparation et récompense narrative. (Usage : Film, Animation)
25. Point de Vue Narratif : Qui conduit émotionnellement. (Usage : Film, Animation, Documentaire)
26. Monde en Conflit : Comment le monde réagit. (Usage : Film, Animation)
27. Fin Émotionnelle : Sensation laissée au public. (Usage : Tous)
`;

export const AUTEUR_PILLARS_KNOWLEDGE = AUTEUR_PILLARS_KNOWLEDGE_PT; // Legacy export

export const PILLAR_LIBRARY: PillarDefinition[] = [
   { id: 'protagonist', number: 1, name: 'Protagonista', description: 'Personagem central que conduz a narrativa.', recommendedFormats: ['Film', 'Animation', 'Documentary', 'Advertisement'] },
   { id: 'objective', number: 2, name: 'Objetivo', description: 'Meta clara e mensurável do protagonista.', recommendedFormats: ['Film', 'Animation', 'Advertisement'] },
   { id: 'antagonist', number: 3, name: 'Antagonista / Força Opositora', description: 'Força que impede o progresso do protagonista.', recommendedFormats: ['Film', 'Animation', 'Advertisement'] },
   { id: 'conflict', number: 4, name: 'Conflito', description: 'Tensão principal da história.', recommendedFormats: ['All'] },
   { id: 'stakes', number: 5, name: 'Stakes', description: 'O que está em jogo se o protagonista falhar.', recommendedFormats: ['Film', 'Animation', 'Advertisement'] },
   { id: 'setting', number: 6, name: 'Cenário / Era', description: 'Onde e quando a história acontece.', recommendedFormats: ['All'] },
   { id: 'theme', number: 7, name: 'Tema', description: 'Ideia central ou argumento da obra.', recommendedFormats: ['Film', 'Animation', 'Documentary'] },
   { id: 'internal_desire', number: 8, name: 'Desejo Interno', description: 'Necessidade emocional do protagonista.', recommendedFormats: ['Film', 'Animation'] },
   { id: 'tragic_flaw', number: 9, name: 'Falha Trágica', description: 'Defeito que gera conflito.', recommendedFormats: ['Film', 'Animation'] },
   { id: 'arc', number: 10, name: 'Arco do Personagem', description: 'Transformação ao longo da história.', recommendedFormats: ['Film', 'Animation'] },
   { id: 'inciting_incident', number: 11, name: 'Incidente Incitante', description: 'Evento que inicia a jornada.', recommendedFormats: ['Film', 'Animation', 'Advertisement'] },
   { id: 'plan', number: 12, name: 'Plano do Protagonista', description: 'Estratégia para atingir o objetivo.', recommendedFormats: ['Film', 'Animation'] },
   { id: 'obstacles', number: 13, name: 'Obstáculos Progressivos', description: 'Dificuldades crescentes.', recommendedFormats: ['Film', 'Animation'] },
   { id: 'no_return', number: 14, name: 'Ponto de Não Retorno', description: 'Decisão irreversível.', recommendedFormats: ['Film', 'Animation'] },
   { id: 'climax', number: 15, name: 'Clímax', description: 'Confronto máximo.', recommendedFormats: ['Film', 'Animation', 'Advertisement'] },
   { id: 'resolution', number: 16, name: 'Resolução', description: 'Novo equilíbrio final.', recommendedFormats: ['Film', 'Animation', 'Advertisement'] },
   { id: 'motif', number: 17, name: 'Motivo Recorrente', description: 'Elemento simbólico repetido.', recommendedFormats: ['Film', 'Animation'] },
   { id: 'irony', number: 18, name: 'Ironia Dramática', description: 'Contraste entre intenção e resultado.', recommendedFormats: ['Film', 'Animation'] },
   { id: 'moral_cost', number: 19, name: 'Regra Moral / Custo Ético', description: 'Preço de vencer.', recommendedFormats: ['Film', 'Animation'] },
   { id: 'genre_promise', number: 20, name: 'Promessa de Gênero', description: 'Expectativa criada no público.', recommendedFormats: ['All'] },
   { id: 'tone', number: 21, name: 'Tom', description: 'Atitude emocional da narrativa.', recommendedFormats: ['All'] },
   { id: 'pacing', number: 22, name: 'Ritmo / Escalada', description: 'Progressão da tensão.', recommendedFormats: ['Film', 'Animation', 'Advertisement'] },
   { id: 'trigger', number: 23, name: 'Gatilho Emocional', description: 'Emoção principal provocada.', recommendedFormats: ['All'] },
   { id: 'plant_payoff', number: 24, name: 'Plant & Payoff', description: 'Preparação e recompensa narrativa.', recommendedFormats: ['Film', 'Animation'] },
   { id: 'pov', number: 25, name: 'Ponto de Vista Narrativo', description: 'Quem conduz emocionalmente.', recommendedFormats: ['Film', 'Animation', 'Documentary'] },
   { id: 'world_conflict', number: 26, name: 'Mundo em Conflito', description: 'Como o mundo reage.', recommendedFormats: ['Film', 'Animation'] },
   { id: 'ending_feeling', number: 27, name: 'Final Emocional', description: 'Sensação deixada no público.', recommendedFormats: ['All'] },
];
